import { useState, type ReactNode } from "react"

import { ExclamationTriangleIcon } from "@heroicons/react/24/solid"
import { isAxiosError } from "axios"
import { clsx } from "clsx"

import {
  isPricingSupersededError,
  PRICING_SUPERSEDED_MESSAGE,
} from "~/api/pricing-errors"
import { parseAxiosError } from "~/utils/parseAxiosError"

import type {
  LightReachFinancingView,
  LightReachOfferLock,
  LightReachOfferPickerBundle,
  LightReachOffersView,
  LightReachOfferStatus,
  LightReachOfferView,
  LightReachQuoteView,
  LightReachSelectionView,
} from "./lightreach-kind-bundle"
import { Icon } from "../../../BatteryOnlyProposal/primitives"
import {
  TPO_PRODUCT_TYPE_LABEL,
  tpoTermsText,
} from "../../../BatteryOnlyProposal/tpo-terms-copy"
import {
  OutOfDateNotice,
  REFRESH_PRICING_BUSY_LABEL,
  REFRESH_PRICING_ERROR_PROMPT,
  REFRESH_PRICING_LABEL,
} from "../../../ProposalPresentation/pricing/pricing-out-of-date"
import {
  btnLink,
  btnPrimary,
  btnSecondary,
  monthlyUsd,
  Pill,
  sectionLabelClass,
} from "../financing-chrome"

export const SAVE_LIGHTREACH_OFFER_LABEL = `Save offer`
export const SAVE_LIGHTREACH_OFFER_BUSY_LABEL = `Saving offer`
export const REFRESH_LIGHTREACH_OFFER_LABEL = `Refresh offer`
export const REFRESH_LIGHTREACH_OFFER_BUSY_LABEL = `Refreshing offer`
export const SAVED_LIGHTREACH_OFFER_LABEL = `Offer saved and payment quoted`
export const SAVED_LIGHTREACH_OFFER_WITHOUT_PAYMENT_LABEL = `Offer saved, but its payment is out of date. Refresh the offer to quote it.`
export const LIGHTREACH_OFFERS_LOADING_LABEL = `Loading LightReach offers`
export const LIGHTREACH_OFFERS_LOAD_ERROR_PROMPT = `Unable to load LightReach offers`
export const LIGHTREACH_PROVIDER_FAILED_PROMPT = `LightReach did not return offers. Try again.`
export const LIGHTREACH_SELECTED_OFFER_UNAVAILABLE_PROMPT = `LightReach no longer returns the saved offer. Choose another to quote a payment.`
export const LIGHTREACH_OFFER_REQUIRED_PROMPT = `Choose an offer to save a Quote.`
export const LIGHTREACH_STALE_OFFER_PROMPT = `The proposal changed after this offer was saved. Refresh it to quote LightReach's payment for the current price.`
export const SAVE_LIGHTREACH_OFFER_ERROR_PROMPT = `Unable to save this offer. The saved Quote stays out of date until a save succeeds.`
export const LIGHTREACH_PAYMENT_OUT_OF_DATE_LABEL = `Out of date`
export const LIGHTREACH_PRICE_CAP_PILL = `Above price cap`
export const LIGHTREACH_FIRST_YEAR_LABEL = `first year`

/** PPA first, then Lease; a group LightReach returned nothing for is omitted. */
const PRODUCT_TYPE_ORDER: readonly LightReachOfferView[`productType`][] = [
  `ppa`,
  `lease`,
]

/** `available` is absent: nothing needs saying when offers are usable. */
const statusPrompts = (
  copy: LightReachOfferPickerBundle<unknown>[`copy`],
): Partial<Record<LightReachOfferStatus, string>> => ({
  empty: copy.empty,
  pricing_required: copy.pricingRequired,
  provider_failed: LIGHTREACH_PROVIDER_FAILED_PROMPT,
  selected_offer_unavailable: LIGHTREACH_SELECTED_OFFER_UNAVAILABLE_PROMPT,
  unavailable: copy.unavailable,
  ...copy.kindStatusPrompts,
})

/** Only a provider answer can change on a plain re-read. */
const RETRYABLE_STATUSES: ReadonlySet<LightReachOffersView[`status`]> = new Set([
  `empty`,
  `provider_failed`,
])

/**
 * The live row for a saved selection. LightReach issues a new productId for
 * every product whenever the priced total changes, and the old id can then
 * name a different product, so terms decide the match: the row with the saved
 * id on the same type, term and escalator, else the only row on those terms.
 * Two rows on the same terms leave nothing to match: the seller chooses again.
 * Mirrors the gateway's matchLightReachOffer.
 */
export const matchLightReachOffer = (
  offers: LightReachOfferView[],
  selected: LightReachSelectionView | null,
): LightReachOfferView | null => {
  if (selected == null) {
    return null
  }
  const sameTerms = offers.filter(
    (offer) =>
      offer.productType === selected.productType &&
      offer.termYears === selected.termYears &&
      offer.escalationRate === selected.escalationRate,
  )
  const byId = sameTerms.find((offer) => offer.productId === selected.productId)
  if (byId) {
    return byId
  }
  return sameTerms.length === 1 ? (sameTerms[0] ?? null) : null
}

/**
 * The server names what it refused — equipment LightReach cannot price, a
 * missing homeowner email, a license
 * LightReach has no record of, a save already in progress — so a 4xx reason
 * is shown as sent. Anything else gets the generic save copy.
 */
const clientErrorReason = (error: unknown): string | null => {
  const status = isAxiosError(error) ? error.response?.status : undefined
  return status != null && status >= 400 && status < 500
    ? parseAxiosError(error, ``) || null
    : null
}

const saveErrorMessage = (error: unknown): string =>
  isPricingSupersededError(error)
    ? PRICING_SUPERSEDED_MESSAGE
    : (clientErrorReason(error) ?? SAVE_LIGHTREACH_OFFER_ERROR_PROMPT)

const PriceCapWarning = ({ warning }: { warning: string }): JSX.Element => (
  <div
    role="status"
    data-testid="lightreach-price-cap-warning"
    className="flex items-start gap-2 rounded-lg border border-bp-gold/40 bg-bp-gold/10 px-3.5 py-3 text-[length:var(--step--2)] text-bp-text"
  >
    <ExclamationTriangleIcon
      aria-hidden="true"
      className="mt-0.5 size-4 shrink-0 text-bp-gold"
    />
    <span>{warning}</span>
  </div>
)

/**
 * Why the offer is locked, from the financing read: a holding application
 * locks all financing; any LightReach application, including a declined one,
 * keeps its offer. Null when neither applies.
 */
export const lightReachOfferLock = (
  financing: LightReachFinancingView | null,
  applicationExistsReason: string,
): LightReachOfferLock | null => {
  if (financing == null) {
    return null
  }
  const reason =
    financing.financingLocked === true
      ? (financing.financingLockReason ?? applicationExistsReason)
      : financing.actions.viewLightReachApplication?.allowed === true
        ? applicationExistsReason
        : null
  return reason == null
    ? null
    : { locked: true, pending: false, reason, statusKind: `financing-status` }
}

/**
 * The LightReach half of the Loan Product drawer: the offers LightReach
 * estimated for the current price, grouped PPA / Lease, and an explicit save.
 * Every payment is LightReach's own figure, read back from the server — none
 * is calculated here.
 */
export const LightReachOfferPicker = <TSaved,>({
  bundle,
  proposalId,
  quote,
  financing,
  onClose,
}: {
  bundle: LightReachOfferPickerBundle<TSaved>
  proposalId: string
  quote: LightReachQuoteView | null
  /** The financing read the drawer already holds; it states the lock. */
  financing: LightReachFinancingView | null
  onClose: () => void
}): JSX.Element => {
  const { copy, LockNotice } = bundle
  const offersQuery = bundle.useOffers(proposalId)
  // A LightReach application locks the offer; the server withholds select
  // and refresh, and the notice says why.
  const financingLock = lightReachOfferLock(financing, copy.applicationExists)
  const saveOffer = bundle.useSave()
  const refreshPricing = bundle.useRefreshPricing()
  // The seller's pick lives here only; reopening the drawer falls back to
  // what the server saved.
  const [draftProductId, setDraftProductId] = useState<string | null>(null)
  const [saved, setSaved] = useState<`quoted` | `saved_only` | null>(null)
  // Which control started the save in flight, so only that one reads busy.
  const [refreshingSaved, setRefreshingSaved] = useState(false)

  const offers = offersQuery.data ?? null
  const status = offers?.status ?? null
  // Offers and their pricing context describe one price. While a refetch is in
  // flight the price may already have moved, so nothing is selected or saved.
  const settling = offersQuery.isFetching
  const rows =
    status === `available` || status === `selected_offer_unavailable`
      ? (offers?.offers ?? [])
      : []
  const savedSelection = offers?.selectedOffer ?? null
  const savedRow = matchLightReachOffer(rows, savedSelection)
  const busy = saveOffer.isLoading || refreshPricing.isLoading || settling
  const canEdit = offers?.canSelect ?? false
  const canRefresh = offers?.canRefreshPricing ?? false
  const pricingContext = offers?.pricingContext ?? null
  const priceCap = offers?.priceCap?.exceeded === true ? offers.priceCap : null

  const selectedProductId = draftProductId ?? savedRow?.productId ?? null
  const chosen =
    rows.find((offer) => offer.productId === selectedProductId) ?? null
  const groups = PRODUCT_TYPE_ORDER.map((productType) => ({
    productType,
    offers: rows
      .filter((offer) => offer.productType === productType)
      .sort(
        (left, right) =>
          left.termYears - right.termYears ||
          left.escalationRate - right.escalationRate,
      ),
  })).filter((group) => group.offers.length > 0)

  // The Quote and the saved selection are separate reads. A save updates the
  // Quote first, so while they disagree the pair would describe the offer the
  // seller just replaced — say nothing rather than something wrong.
  const savedQuote =
    quote != null &&
    savedSelection != null &&
    quote.loanProduct?.lightReach?.productId === savedSelection.productId
      ? quote
      : null
  const showSavedSelection =
    savedSelection != null && (savedQuote != null || quote?.loanProduct == null)
  const savedQuoteStale = savedQuote?.status === `stale`
  // A stale saved offer is refreshed by saving it again at the current price,
  // which needs LightReach to still return it for that price.
  const canRefreshSaved =
    savedQuoteStale && savedRow != null && canEdit && pricingContext != null
  // Re-saving the offer behind a current Quote would only void and recreate
  // the same LightReach quote.
  const alreadySaved =
    chosen != null &&
    chosen.productId === savedRow?.productId &&
    savedQuote?.status === `current`
  const submittable = chosen != null && pricingContext != null && !alreadySaved
  const showSaveAction = canEdit && rows.length > 0

  // The server matches the saved offer by productId, which LightReach
  // reissues whenever the price moves. While the same terms are still
  // offered the saved offer is only stale, not gone.
  const savedOfferGone =
    status === `selected_offer_unavailable` && savedRow == null
  // A status this bundle does not model still owes the seller a reason.
  const statusPrompt =
    offers == null ||
    status === `available` ||
    (status === `selected_offer_unavailable` && !savedOfferGone)
      ? null
      : (statusPrompts(copy)[offers.status] ?? LIGHTREACH_PROVIDER_FAILED_PROMPT)
  const canRetryOffers =
    statusPrompt != null && status != null && RETRYABLE_STATUSES.has(status)

  const save = (
    offer: LightReachOfferView | null,
    { refresh = false }: { refresh?: boolean } = {},
  ): void => {
    if (!canEdit || busy || offer == null || pricingContext == null) {
      return
    }
    setRefreshingSaved(refresh)
    saveOffer.mutate(
      { proposalId, body: { productId: offer.productId, pricingContext } },
      {
        onSuccess: (result) => {
          // The server's saved selection takes over from the local pick.
          setDraftProductId(null)
          // A 200 whose Quote came back without a current payment — a newer
          // write landed first — must not be announced as one.
          setSaved(bundle.savedOutcome(result))
        },
      },
    )
  }

  return (
    <>
      <div className="flex flex-col gap-4">
        <h3 className={sectionLabelClass}>LightReach offer</h3>

        {offersQuery.isLoading ? (
          <p
            role="status"
            className="text-[length:var(--step--1)] text-bp-text-muted"
          >
            {LIGHTREACH_OFFERS_LOADING_LABEL}
          </p>
        ) : null}

        {offersQuery.isError ? (
          <div className="flex flex-col gap-2">
            <p
              role="alert"
              className="text-[length:var(--step--2)] text-bp-danger"
            >
              {clientErrorReason(offersQuery.error) ??
                LIGHTREACH_OFFERS_LOAD_ERROR_PROMPT}
            </p>
            <button
              type="button"
              onClick={() => {
                void offersQuery.refetch()
              }}
              className={btnLink}
            >
              Retry
            </button>
          </div>
        ) : null}

        {financingLock != null ? (
          <LockNotice proposalId={proposalId} lock={financingLock} />
        ) : null}

        {priceCap ? (
          <PriceCapWarning
            warning={copy.priceCapWarning(priceCap, { canSave: canEdit })}
          />
        ) : null}

        {showSavedSelection && savedSelection ? (
          <SavedSelection
            selection={savedSelection}
            name={savedRow?.name ?? savedQuote?.loanProduct?.name ?? null}
            quote={savedQuote}
            unavailable={savedOfferGone}
            overPriceCap={priceCap != null}
            refresh={
              canRefreshSaved
                ? {
                    busy: saveOffer.isLoading && refreshingSaved,
                    disabled: busy,
                    onRefresh: () => {
                      setSaved(null)
                      setDraftProductId(null)
                      save(savedRow, { refresh: true })
                    },
                  }
                : null
            }
          />
        ) : null}

        {statusPrompt ? (
          <div className="flex flex-col items-start gap-2">
            <p
              role="status"
              className={clsx(
                `text-[length:var(--step--2)]`,
                status === `provider_failed`
                  ? `text-bp-danger`
                  : `text-bp-text-muted`,
              )}
            >
              {statusPrompt}
            </p>
            {canRetryOffers ? (
              <button
                type="button"
                onClick={() => {
                  void offersQuery.refetch()
                }}
                className={btnLink}
              >
                Try again
              </button>
            ) : null}
          </div>
        ) : null}

        {groups.map((group) => (
          <fieldset
            key={group.productType}
            className="flex flex-col gap-2"
            data-testid={`lightreach-offer-group-${group.productType}`}
          >
            <legend className="mb-2 text-[length:var(--step--2)] font-semibold tracking-[0.12em] text-bp-text-muted uppercase">
              {TPO_PRODUCT_TYPE_LABEL[group.productType]}
            </legend>
            <div className="flex flex-col gap-2">
              {group.offers.map((offer) => (
                <LightReachOfferRow
                  key={offer.productId}
                  offer={offer}
                  value={offer.productId}
                  checked={offer.productId === chosen?.productId}
                  unsaved={
                    offer.productId === chosen?.productId &&
                    offer.productId !== savedRow?.productId
                  }
                  disabled={!canEdit || busy}
                  onSelect={() => {
                    saveOffer.reset()
                    setSaved(null)
                    setDraftProductId(offer.productId)
                  }}
                />
              ))}
            </div>
          </fieldset>
        ))}

        {rows.length > 0 && !canEdit && financingLock == null ? (
          <p
            role="status"
            className="text-[length:var(--step--2)] text-bp-text-muted"
          >
            {copy.readOnly}
          </p>
        ) : null}

        {rows.length > 0 && canEdit && chosen == null ? (
          <p
            role="status"
            className="text-[length:var(--step--2)] text-bp-text-muted"
          >
            {LIGHTREACH_OFFER_REQUIRED_PROMPT}
          </p>
        ) : null}
      </div>

      <div className="mt-auto flex shrink-0 flex-col gap-3 border-t border-bp-border pt-5">
        {saveOffer.isError ? (
          <div
            role="alert"
            className="flex items-center justify-between gap-3 rounded-md border border-bp-danger/40 bg-bp-danger/10 px-3 py-2"
          >
            <span className="text-[length:var(--step--2)] text-bp-danger">
              {saveErrorMessage(saveOffer.error)}
            </span>
            {/* A superseded save re-read offers at a new price; retrying
                would save the seller's pick at a payment they never saw. */}
            {isPricingSupersededError(saveOffer.error) ? null : (
              <button
                type="button"
                disabled={chosen == null || pricingContext == null || busy}
                onClick={() => {
                  save(chosen)
                }}
                className="shrink-0 rounded-md border border-bp-danger/50 px-3 py-1 text-[length:var(--step--2)] font-medium text-bp-text disabled:cursor-not-allowed disabled:opacity-50"
              >
                Retry
              </button>
            )}
          </div>
        ) : null}
        {refreshPricing.isError ? (
          <p
            role="alert"
            className="text-[length:var(--step--2)] text-bp-danger"
          >
            {isPricingSupersededError(refreshPricing.error)
              ? PRICING_SUPERSEDED_MESSAGE
              : REFRESH_PRICING_ERROR_PROMPT}
          </p>
        ) : null}
        {saved ? (
          <p
            role="status"
            className={clsx(
              `flex items-center gap-1.5 text-[length:var(--step--2)] font-medium`,
              saved === `quoted` ? `text-bp-success` : `text-bp-text-muted`,
            )}
          >
            <Icon name="check" className="size-4" />
            {saved === `quoted`
              ? SAVED_LIGHTREACH_OFFER_LABEL
              : SAVED_LIGHTREACH_OFFER_WITHOUT_PAYMENT_LABEL}
          </p>
        ) : null}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <button type="button" onClick={onClose} className={btnSecondary}>
            Close
          </button>
          {showSaveAction ? (
            <button
              type="button"
              disabled={!submittable || busy}
              onClick={() => {
                save(chosen)
              }}
              className={btnPrimary}
            >
              {saveOffer.isLoading && !refreshingSaved
                ? SAVE_LIGHTREACH_OFFER_BUSY_LABEL
                : SAVE_LIGHTREACH_OFFER_LABEL}
            </button>
          ) : canRefresh && status === `pricing_required` ? (
            <button
              type="button"
              disabled={busy}
              onClick={() => {
                saveOffer.reset()
                setSaved(null)
                refreshPricing.mutate({ proposalId })
              }}
              className={btnPrimary}
            >
              {refreshPricing.isLoading
                ? REFRESH_PRICING_BUSY_LABEL
                : REFRESH_PRICING_LABEL}
            </button>
          ) : null}
        </div>
      </div>
    </>
  )
}

/**
 * One returned offer. LightReach's first-year payment is printed to the cent
 * exactly as it arrived; nothing here escalates or rounds it. A kind whose
 * offers carry more (Solar's tier and $/kWh) adds it as `detail`, and its own
 * markers (Suggested) as `badge`.
 */
export const LightReachOfferRow = ({
  offer,
  value,
  checked,
  unsaved,
  disabled,
  onSelect,
  detail = null,
  badge = null,
}: {
  offer: Pick<
    LightReachOfferView,
    `escalationRate` | `firstYearMonthlyPayment` | `name` | `termYears`
  >
  /** The radio's value: the id the kind selects the offer by. */
  value: string
  checked: boolean
  unsaved: boolean
  disabled: boolean
  onSelect: () => void
  detail?: ReactNode
  badge?: ReactNode
}): JSX.Element => (
  <label
    data-testid="lightreach-offer-row"
    className={clsx(
      `flex cursor-pointer items-center justify-between gap-3 rounded-lg border px-3.5 py-3 transition-colors`,
      checked
        ? `border-bp-accent bg-bp-accent/10`
        : `border-bp-border bg-bp-elevated hover:border-bp-accent`,
      disabled && `cursor-not-allowed opacity-60`,
    )}
  >
    <span className="flex min-w-0 items-center gap-3">
      <input
        type="radio"
        name="lightreach-offer"
        className="size-4 shrink-0 accent-bp-accent"
        value={value}
        checked={checked}
        disabled={disabled}
        onChange={onSelect}
      />
      <span className="flex min-w-0 flex-col">
        <span className="text-[length:var(--step--1)] text-bp-text">
          {offer.name}
        </span>
        <span className="text-[length:var(--step--2)] text-bp-text-muted">
          {tpoTermsText(offer)}
        </span>
        {detail != null ? (
          <span className="text-[length:var(--step--2)] text-bp-text-muted tabular-nums">
            {detail}
          </span>
        ) : null}
      </span>
    </span>
    <span className="flex shrink-0 items-center gap-2">
      {badge}
      {unsaved ? <Pill tone="warn">Unsaved</Pill> : null}
      <span className="flex flex-col items-end">
        <span className="text-[length:var(--step--1)] font-semibold text-bp-text tabular-nums">
          {`${monthlyUsd.format(offer.firstYearMonthlyPayment)}/mo`}
        </span>
        <span className="text-[length:var(--step--3)] text-bp-text-muted">
          {LIGHTREACH_FIRST_YEAR_LABEL}
        </span>
      </span>
    </span>
  </label>
)

/**
 * The saved offer and the Quote it produced. A retained Quote keeps its
 * payment on screen, marked out of date, with a refresh when LightReach still
 * returns the offer for the current price.
 */
const SavedSelection = ({
  selection,
  name,
  quote,
  unavailable,
  overPriceCap,
  refresh,
}: {
  selection: LightReachSelectionView
  name: string | null
  quote: LightReachQuoteView | null
  unavailable: boolean
  overPriceCap: boolean
  refresh: { busy: boolean; disabled: boolean; onRefresh: () => void } | null
}): JSX.Element => {
  // The Quote's own snapshot wins: it is what LightReach actually quoted.
  const terms = quote?.tpoTerms ?? selection
  const stale = quote?.status === `stale`
  return (
    <div
      data-testid="lightreach-saved-offer"
      className={clsx(
        `flex flex-col gap-3 rounded-lg border px-3.5 py-3`,
        unavailable
          ? `border-bp-danger/40 bg-bp-danger/5`
          : `border-bp-border bg-bp-elevated`,
      )}
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <span className="flex min-w-0 flex-col gap-0.5">
          <span className="text-[length:var(--step--1)] font-semibold text-bp-text">
            {TPO_PRODUCT_TYPE_LABEL[terms.productType]}
            {name ? ` · ${name}` : ``}
          </span>
          <span className="text-[length:var(--step--2)] text-bp-text-muted">
            {tpoTermsText(terms)}
          </span>
          {quote?.estimatedMonthlyPayment != null ? (
            <span className="flex flex-wrap items-center gap-2 text-[length:var(--step--2)] text-bp-text-muted">
              <span
                data-testid="lightreach-saved-payment"
                className="text-bp-text tabular-nums"
              >
                {`${monthlyUsd.format(quote.estimatedMonthlyPayment)}/mo`}
              </span>
              <span>{LIGHTREACH_FIRST_YEAR_LABEL}</span>
              {stale ? (
                <OutOfDateNotice label={LIGHTREACH_PAYMENT_OUT_OF_DATE_LABEL} />
              ) : null}
            </span>
          ) : null}
        </span>
        <span className="flex shrink-0 flex-wrap items-center gap-2">
          <Pill tone="muted">Saved</Pill>
          {overPriceCap ? (
            <Pill tone="warn">{LIGHTREACH_PRICE_CAP_PILL}</Pill>
          ) : null}
          {unavailable ? <Pill tone="danger">Unavailable</Pill> : null}
        </span>
      </div>
      {/* The prompt names an action, so it only shows beside one. */}
      {stale && refresh ? (
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-bp-border pt-3">
          <p
            role="status"
            className="text-[length:var(--step--2)] text-bp-text-muted"
          >
            {LIGHTREACH_STALE_OFFER_PROMPT}
          </p>
          <button
            type="button"
            disabled={refresh.disabled}
            onClick={refresh.onRefresh}
            className={btnLink}
          >
            {refresh.busy
              ? REFRESH_LIGHTREACH_OFFER_BUSY_LABEL
              : REFRESH_LIGHTREACH_OFFER_LABEL}
          </button>
        </div>
      ) : null}
    </div>
  )
}
